/**
 * /share/sound/:id?token=… — public, read-only player for an Audio Studio sound. No login: the audio API checks the
 * token and that the owner left link sharing on (GET /public/tracks/{id}?token=).
 */
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Lock, Music2 } from 'lucide-react';
import { audioStudioApi } from '@/api/audioStudioApi';
import { capitalize, formatClock } from './format';

export function PublicSoundPage() {
  const { id = '' } = useParams<{ id: string }>();
  const token = useMemo(() => new URLSearchParams(window.location.search).get('token') || '', []);
  const q = useQuery({
    queryKey: ['audio-studio', 'public', id, token],
    queryFn: () => audioStudioApi.publicTrack(id, token),
    enabled: Boolean(id && token),
    retry: false,
  });
  useEffect(() => { if (q.data?.title) document.title = `${q.data.title} · Beatly`; }, [q.data?.title]);

  if (id && token && q.isLoading) {
    return <div className="flex min-h-screen items-center justify-center bg-[#111318] text-[13px] text-[#98a2b3]"><Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> Loading…</div>;
  }
  if (!id || !token || q.isError || !q.data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f8f9fc] px-6">
        <div className="max-w-md rounded-2xl border border-[#eaecf0] bg-white p-8 text-center shadow-sm">
          <Lock className="mx-auto h-6 w-6 text-[#98a2b3]" aria-hidden />
          <div className="mt-3 text-[16px] font-semibold text-[#101828]">This sound is not available</div>
          <div className="mt-1.5 text-[13px] text-[#667085]">{!token ? 'This link is incomplete.' : (q.error as Error | null)?.message || 'The link may have been reset or turned off.'}</div>
          <div className="mt-4 text-[11.5px] text-[#98a2b3]">Ask whoever sent you the link to turn link sharing back on in Beatly.</div>
        </div>
      </div>
    );
  }

  const t = q.data;
  const meta = [capitalize(t.genre), capitalize(t.mood), t.duration_s ? formatClock(t.duration_s) : null].filter(Boolean);
  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-[#1b0730] via-[#111318] to-[#111318] text-white">
      <div className="flex items-center gap-3 border-b border-white/10 px-5 py-2.5 text-[12px] text-[#98a2b3]">
        <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-semibold text-white">Shared sound</span>
        <span className="min-w-0 flex-1" />
        <a href="https://beatly.com" target="_blank" rel="noreferrer" className="font-semibold text-white hover:underline">Made with Beatly</a>
      </div>
      <main className="flex flex-1 items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-md rounded-3xl border border-white/10 bg-white/5 p-5 shadow-[0_30px_80px_rgba(0,0,0,0.45)] backdrop-blur sm:p-7">
          <div className="aspect-square w-full overflow-hidden rounded-2xl bg-gradient-to-br from-[#be43ff] to-[#3b0764]">
            {t.cover_url ? <img src={t.cover_url} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><Music2 className="h-16 w-16 text-white/70" aria-hidden /></div>}
          </div>
          <h1 className="mt-5 text-[22px] leading-tight" style={{ fontWeight: 700 }}>{t.title || 'Untitled'}</h1>
          {meta.length > 0 && <div className="mt-1 text-[13px] text-white/60">{meta.join(' · ')}</div>}
          {t.description && <p className="mt-3 text-[13px] leading-relaxed text-white/75">{t.description}</p>}
          {t.audio_url && <audio controls preload="metadata" src={t.audio_url} className="mt-5 w-full" />}
        </div>
      </main>
    </div>
  );
}
